import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import axiosInstance from "../../utils/axios";

type ProductKey = "aurausviitta" | "murske" | "liikennemerkki" | "muu";

interface ProcurementInterest {
  id: number;
  productKey: string;
  quantityEstimate: string | null;
}

const PRODUCTS: {
  key: ProductKey;
  title: string;
  pitch: string;
  quantityLabel?: string;
  notesLabel?: string;
}[] = [
  {
    key: "aurausviitta",
    title: "Aurausviitat",
    pitch:
      "Syksyn 2026 yhteistilaus: laadukkaat heijastimelliset aurausviitat toimitettuna. Hinta tarkentuu kiinnostuksen perusteella.",
    quantityLabel: "Arvioitu määrä (kpl)",
  },
  {
    key: "murske",
    title: "Murske",
    pitch:
      "Kevään 2027 kilpailutus: pyydämme tarjoukset paikallisilta toimittajilta tiekuntanne puolesta. Hinta tarkentuu kiinnostuksen perusteella.",
    quantityLabel: "Arvioitu määrä (tn)",
  },
  {
    key: "liikennemerkki",
    title: "Liikennemerkit",
    pitch:
      "Tieliikennelain mukaiset liikennemerkit ja kiinnitystarvikkeet tiekunnille. Hinta tarkentuu kiinnostuksen perusteella.",
  },
  {
    key: "muu",
    title: "Muu hankinta",
    pitch:
      "Puuttuuko listalta jotain? Kerro, mitä muuta tiekuntanne haluaisi hankkia yhteishankintana.",
    notesLabel: "Mitä haluaisitte hankkia?",
  },
];

function ProductCard({
  product,
  submitted,
  onSubmit,
}: {
  product: (typeof PRODUCTS)[number];
  submitted: boolean;
  onSubmit: (
    productKey: ProductKey,
    quantityEstimate: string,
    notes: string
  ) => Promise<void>;
}) {
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  // "Muu" is meaningless without a description
  const disabled = sending || (Boolean(product.notesLabel) && !notes.trim());

  const handleClick = async () => {
    setSending(true);
    try {
      await onSubmit(product.key, quantity, notes);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent
        sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}
      >
        <Typography variant="h6">{product.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {product.pitch}
        </Typography>
        {submitted ? (
          <Alert icon={<CheckCircleOutline />} severity="success">
            Kiitos! Kiinnostus kirjattu — olemme yhteydessä.
          </Alert>
        ) : (
          <>
            {product.quantityLabel && (
              <TextField
                label={product.quantityLabel}
                size="small"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            )}
            {product.notesLabel && (
              <TextField
                label={product.notesLabel}
                size="small"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
            <Button
              variant="contained"
              onClick={handleClick}
              disabled={disabled}
            >
              {sending ? "Lähetetään..." : "Ilmoita kiinnostus"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Hankinnat() {
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [interests, setInterests] = useState<ProcurementInterest[] | null>(
    null
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!associationId) return;
    let cancelled = false;
    axiosInstance
      .get<ProcurementInterest[]>(
        `/associations/${associationId}/procurement-interests`
      )
      .then((res) => {
        if (!cancelled) setInterests(res.data);
      })
      .catch(() => {
        if (!cancelled) setInterests([]);
      });
    return () => {
      cancelled = true;
    };
  }, [associationId]);

  const handleSubmit = useCallback(
    async (productKey: ProductKey, quantityEstimate: string, notes: string) => {
      if (!associationId) return;
      try {
        const res = await axiosInstance.post<ProcurementInterest>(
          `/associations/${associationId}/procurement-interests`,
          {
            productKey,
            quantityEstimate: quantityEstimate || null,
            notes: notes || null,
          }
        );
        setError(false);
        setInterests((prev) => [
          ...(prev ?? []).filter((i) => i.productKey !== productKey),
          res.data,
        ]);
      } catch (err) {
        console.error("Kiinnostuksen lähetys epäonnistui:", err);
        setError(true);
      }
    },
    [associationId]
  );

  const submittedKeys = new Set((interests ?? []).map((i) => i.productKey));

  return (
    <>
      <Helmet title="Hankinnat" />
      <Typography variant="h3" gutterBottom>
        Hankinnat tiekunnalle
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Selvitämme yhteishankintoja tiekunnille — ilmoita kiinnostuksesi, niin
        olemme yhteydessä kun hankinta käynnistyy. Ilmoittautuminen ei sido
        mihinkään.
      </Typography>
      <Divider sx={{ my: 4 }} />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Lähetys epäonnistui. Yritä uudelleen.
        </Alert>
      )}
      {interests === null ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {PRODUCTS.map((product) => (
            <Grid item xs={12} sm={6} lg={3} key={product.key}>
              <ProductCard
                product={product}
                submitted={submittedKeys.has(product.key)}
                onSubmit={handleSubmit}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}
